import { CheckIcon } from "@heroicons/react/24/outline"
import React, { useState, useEffect } from "react"

import { Button } from "../../../../components/shared/Buttons"
import ConfirmSaveModal from "../../components/shared/ConfirmSaveModal"

const FontsForm = ({ theme, onUpdate, onClose }) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const fontPairs = [
    {
      id: "playfair-work",
      primary: "Playfair Display",
      secondary: "Work Sans",
      primaryFont: "Playfair Display, serif",
      secondaryFont: "Work Sans, sans-serif"
    },
    {
      id: "inter-libre",
      primary: "Inter",
      secondary: "Libre Franklin",
      primaryFont: "Inter, sans-serif",
      secondaryFont: "Libre Franklin, sans-serif"
    },
    {
      id: "poppins-roboto",
      primary: "Poppins",
      secondary: "Roboto",
      primaryFont: "Poppins, sans-serif",
      secondaryFont: "Roboto, sans-serif"
    },
    {
      id: "montserrat-open",
      primary: "Montserrat",
      secondary: "Open Sans",
      primaryFont: "Montserrat, sans-serif",
      secondaryFont: "Open Sans, sans-serif"
    },
    {
      id: "opensans-lato",
      primary: "Open Sans",
      secondary: "Lato",
      primaryFont: "Open Sans, sans-serif",
      secondaryFont: "Lato, sans-serif"
    },
    {
      id: "arima-madurai",
      primary: "Arima Madurai",
      secondary: "Source Sans Pro",
      primaryFont: "Arima Madurai, cursive",
      secondaryFont: "Source Sans Pro, sans-serif"
    },
    {
      id: "cabin-roboto",
      primary: "Cabin",
      secondary: "Roboto",
      primaryFont: "Cabin, sans-serif",
      secondaryFont: "Roboto, sans-serif"
    },
    {
      id: "ebgaramond-opensans",
      primary: "EB Garamond",
      secondary: "Open Sans",
      primaryFont: "EB Garamond, serif",
      secondaryFont: "Open Sans, sans-serif"
    },
    {
      id: "jost-inter",
      primary: "Jost",
      secondary: "Inter",
      primaryFont: "Jost, sans-serif",
      secondaryFont: "Inter, sans-serif"
    },
    {
      id: "nunito-opensans",
      primary: "Nunito",
      secondary: "Open Sans",
      primaryFont: "Nunito, sans-serif",
      secondaryFont: "Open Sans, sans-serif"
    }
  ]

  // Function to find the font pair ID based on current theme fonts
  const findFontPairId = (headingFont, paragraphFont) => {
    const pair = fontPairs.find((pair) => pair.primaryFont === headingFont && pair.secondaryFont === paragraphFont)
    return pair ? pair.id : "playfair-work" // Default fallback
  }

  // Initialize selected font pair from current theme
  const initialFontPairId = findFontPairId(theme?.fonts?.heading, theme?.fonts?.paragraph)
  const [selectedFontPair, setSelectedFontPair] = useState(initialFontPairId)

  // Update selected font pair when theme changes
  useEffect(() => {
    const newFontPairId = findFontPairId(theme?.fonts?.heading, theme?.fonts?.paragraph)
    setSelectedFontPair(newFontPairId)
  }, [theme?.fonts?.heading, theme?.fonts?.paragraph])

  const handleFontSelect = (fontPairId) => {
    setSelectedFontPair(fontPairId)

    // Update the renderer immediately with temporary preview
    const selectedPair = fontPairs.find((pair) => pair.id === fontPairId)
    onUpdate(
      {
        fonts: {
          heading: selectedPair.primaryFont,
          paragraph: selectedPair.secondaryFont,
          selectedPair: fontPairId
        }
      },
      { temporary: true }
    )
  }

  const handleSave = () => {
    setShowConfirmModal(true)
  }

  const handleConfirmSave = () => {
    setShowConfirmModal(false)
    const selectedPair = fontPairs.find((pair) => pair.id === selectedFontPair)
    onUpdate({
      fonts: {
        heading: selectedPair.primaryFont,
        paragraph: selectedPair.secondaryFont,
        selectedPair: selectedFontPair
      }
    })
    onClose() // Close the form after saving
  }

  const handleCancelClick = () => {
    // Reset to the stored font when closing without saving
    const storedFontPairId = findFontPairId(theme?.fonts?.heading, theme?.fonts?.paragraph)
    if (selectedFontPair !== storedFontPairId) {
      const storedPair = fontPairs.find((pair) => pair.id === storedFontPairId)
      onUpdate(
        {
          fonts: {
            heading: storedPair.primaryFont,
            paragraph: storedPair.secondaryFont,
            selectedPair: storedFontPairId
          }
        },
        { temporary: true }
      )
    }
    onClose()
  }

  return (
    <div className="w-full">
      {/* Font List */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="space-y-3">
          {fontPairs.map((fontPair) => {
            const isSelected = selectedFontPair === fontPair.id

            return (
              <div
                key={fontPair.id}
                className={`rounded-lg border-2 p-4 transition-all ${
                  isSelected ? "border-teal bg-teal/5" : "border-gray-light bg-white shadow-sm hover:shadow-md"
                }`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="text-lg font-bold text-gray-dark" style={{ fontFamily: fontPair.primaryFont }}>
                      {fontPair.primary}
                    </div>
                    <div className="text-sm text-gray" style={{ fontFamily: fontPair.secondaryFont }}>
                      {fontPair.secondary}
                    </div>
                  </div>

                  {isSelected ? (
                    <div className="flex items-center space-x-2">
                      <CheckIcon className="h-5 w-5 text-teal" />
                      <span className="text-sm font-medium text-teal">Selected</span>
                    </div>
                  ) : (
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => handleFontSelect(fontPair.id)}
                      className="flex items-center justify-center px-4 py-2 text-center">
                      Select
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="sticky bottom-0 z-10 border-t border-gray-light bg-white px-6 py-4">
        <div className="flex items-center justify-end space-x-3">
          <Button type="secondary" onClick={handleCancelClick} className="px-6">
            Close
          </Button>
          <Button type="primary" onClick={handleSave} className="px-6">
            Save
          </Button>
        </div>
      </div>

      {/* Confirm Save Modal */}
      <ConfirmSaveModal
        visible={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSave}
      />
    </div>
  )
}

export default FontsForm
